'use client';

import type { ReactNode } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { BotIcon, Loading03Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../../../components/badge';
import { Tooltip } from '../../../../../components/tooltip';
import { MetaDefault } from '../../../../../components/typography';
import type { TerminalSessionInfo } from './terminal-session-info';

function TerminalSessionBadge({
  info,
}: {
  info: TerminalSessionInfo;
}): ReactNode {
  const tChat = useTranslations('designSystem.chat');

  if (info.kind === 'background') {
    return (
      <Tooltip
        title={tChat('runningInBackgroundTooltip', {
          jobId: info.jobId ?? '',
        })}
        maxW="320px"
      >
        <HStack gap={1} alignItems="center" color="fg.muted">
          <Loading03Icon size={12} />
          <MetaDefault as="span" color="fg.muted" whiteSpace="nowrap">
            {tChat('runningInBackground')}
          </MetaDefault>
        </HStack>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tChat('interactiveSessionTooltip')} maxW="320px">
      <Badge
        variant="outline"
        size="xs"
        icon={<BotIcon size={12} />}
        color="fg.muted"
      >
        {tChat('interactiveSession')}
      </Badge>
    </Tooltip>
  );
}

export function withTerminalSessionBadge({
  info,
  actions,
}: {
  info: TerminalSessionInfo | undefined;
  actions: ReactNode;
}): ReactNode {
  const showsBadge =
    info?.kind === 'background' ||
    (info?.kind === 'interactive' && info.opensSession);

  if (!info || !showsBadge) {
    return actions;
  }

  return (
    <HStack gap={1} alignItems="center">
      <TerminalSessionBadge info={info} />
      {actions}
    </HStack>
  );
}
